import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueue } from '../contexts/QueueContext';
import { useUser } from '../contexts/UserContext';
import { useSipNumber } from '../contexts/SipNumberContext';
import { useLocation } from '../contexts/LocationContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Avatar,
  Divider,
} from '@mui/material';
import Grid2 from '@mui/material/Grid';
import {
  IconArrowLeft,
  IconUserPlus,
  IconEdit,
  IconTrash,
  IconMusic,
  IconSearch,
  IconCheck,
  IconX,
  IconUsers,
  IconPhone,
  IconFileText,
  IconUser
} from '@tabler/icons-react';

interface QueueFormData {
  name: string;
  description: string;
  greetingMessage: string;
  asteriskName: string;
  locationIds: string[];
  priority: string;
  maxWaitTime: number;
  status: string;
  outboundPrefix: string;
  assignedAgentIds: string[];
  assignedSipIds: string[];
  customerCardApiUrl: string;
  customerCardDatasetListUrl: string;
  customerCardDatasetSchemaUrl: string;
  customerCardDatasetAddUrl: string;
  customerCardSelectedDatasetId: string;
  finishCodes?: any[];
}

const QueueManagement: React.FC = () => {
  const navigate = useNavigate();
  const queueContext = useQueue();
  const userContext = useUser();
  const sipNumberContext = useSipNumber();
  const locationContext = useLocation();
  
  const { queues, loading, error, fetchQueues, createQueue, updateQueue, deleteQueue } = queueContext;
  const { users } = userContext;
  const { sipNumbers } = sipNumberContext;
  const { locations } = locationContext;
  
  const [openDialog, setOpenDialog] = useState(false);
  const [editingQueue, setEditingQueue] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/login');
      return;
    }
    
    fetchQueues();
    locationContext.fetchLocations();
  }, []);

  const DEFAULT_FINISH_CODES = [
    { name: 'Görüşme Yapıldı', color: '#10b981', requireNote: false, isActive: true },
    { name: 'Ses Yok', color: '#94a3b8', requireNote: false, isActive: true },
    { name: 'Kara Liste', color: '#ef4444', requireNote: true, isActive: true },
    { name: 'Numara Kullanılmıyor', color: '#f97316', requireNote: false, isActive: true },
    { name: 'Meşgul', color: '#f59e0b', requireNote: false, isActive: true },
    { name: 'Ulaşılamadı', color: '#8b5cf6', requireNote: false, isActive: true },
    { name: 'Daha Sonra Aranacak', color: '#3b82f6', requireNote: true, isActive: true }
  ];

  const [formData, setFormData] = useState<QueueFormData>({
    name: '',
    description: '',
    greetingMessage: 'Hoş geldiniz. Lütfen bekleyiniz, en kısa sürede size yardımcı olacağız.',
    asteriskName: '',
    locationIds: [] as string[],
    priority: 'medium',
    maxWaitTime: 300,
    status: 'active',
    outboundPrefix: '',
    assignedAgentIds: [] as string[],
    assignedSipIds: [] as string[], // SIP numaraları kuyruğa atanır
    customerCardApiUrl: '',
    customerCardDatasetListUrl: '',
    customerCardDatasetSchemaUrl: '',
    customerCardDatasetAddUrl: '',
    customerCardSelectedDatasetId: '',
    finishCodes: DEFAULT_FINISH_CODES
  });

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#F44336';
      case 'medium': return '#FF9800';
      case 'low': return '#4CAF50';
      default: return '#666';
    }
  };

  const getPriorityText = (priority: string) => {
    switch (priority) {
      case 'high': return 'Yüksek';
      case 'medium': return 'Orta';
      case 'low': return 'Düşük';
      default: return 'Belirsiz';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'active' ? '#4CAF50' : '#F44336';
  };

  const getStatusText = (status: string) => {
    return status === 'active' ? 'Aktif' : 'Pasif';
  };

  const formatWaitTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const filteredQueues = queues.filter(queue => {
    const matchesSearch = queue.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (queue.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || queue.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const [newCodeName, setNewCodeName] = useState('');
  const [newCodeColor, setNewCodeColor] = useState('#3b82f6');
  const [newCodeRequireNote, setNewCodeRequireNote] = useState(false);

  const handleAddQueue = () => {
    setEditingQueue(null);
    setFormData({
      name: '',
      description: '',
      greetingMessage: 'Hoş geldiniz. Lütfen bekleyiniz, en kısa sürede size yardımcı olacağız.',
      asteriskName: '',
      locationIds: [],
      priority: 'medium',
      maxWaitTime: 300,
      status: 'active',
      outboundPrefix: '',
      assignedAgentIds: [],
      assignedSipIds: [],
      customerCardApiUrl: '',
      customerCardDatasetListUrl: '',
      customerCardDatasetSchemaUrl: '',
      customerCardDatasetAddUrl: '',
      customerCardSelectedDatasetId: '',
      finishCodes: [...DEFAULT_FINISH_CODES]
    });
    setOpenDialog(true);
  };

  const handleEditQueue = (queue: any) => {
    setEditingQueue(queue);

    // Extract IDs from populated fields
    const locationIds = (queue.locationIds || []).map((loc: any) => 
      typeof loc === 'object' ? loc._id : loc
    );
    
    const assignedAgentIds = (queue.assignedAgentIds || []).map((agent: any) => 
      typeof agent === 'object' ? agent._id : agent
    );
    
    const assignedSipIds = (queue.assignedSipIds || []).map((sip: any) => 
      typeof sip === 'object' ? sip._id : sip
    );

    setFormData({
      name: queue.name,
      asteriskName: queue.asteriskName || queue.name?.replace(/\s+/g, '_').toLowerCase() || '',
      description: queue.description || '',
      greetingMessage: queue.greetingMessage || 'Hoş geldiniz. Lütfen bekleyiniz, en kısa sürede size yardımcı olacağız.',
      locationIds: locationIds,
      priority: queue.priority,
      maxWaitTime: queue.maxWaitTime,
      status: queue.status,
      outboundPrefix: queue.outboundPrefix || '',
      assignedAgentIds: assignedAgentIds,
      assignedSipIds: assignedSipIds,
      customerCardApiUrl: queue.customerCardApiUrl || '',
      customerCardDatasetListUrl: queue.customerCardDatasetListUrl || '',
      customerCardDatasetSchemaUrl: queue.customerCardDatasetSchemaUrl || '',
      customerCardDatasetAddUrl: queue.customerCardDatasetAddUrl || '',
      customerCardSelectedDatasetId: queue.customerCardSelectedDatasetId || '',
      finishCodes: (queue.finishCodes && queue.finishCodes.length > 0) ? queue.finishCodes : [...DEFAULT_FINISH_CODES]
    });
    setOpenDialog(true);
  };

  const handleAddFinishCode = () => {
    if (!newCodeName.trim()) return;
    const newCode = {
      name: newCodeName.trim(),
      color: newCodeColor,
      requireNote: newCodeRequireNote,
      isActive: true
    };
    setFormData((prev: any) => ({
      ...prev,
      finishCodes: [...(prev.finishCodes || []), newCode]
    }));
    setNewCodeName('');
    setNewCodeColor('#3b82f6');
    setNewCodeRequireNote(false);
  };

  const handleRemoveFinishCode = (index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      finishCodes: (prev.finishCodes || []).filter((_: any, i: number) => i !== index)
    }));
  };

  const handleToggleFinishCodeActive = (index: number) => {
    setFormData((prev: any) => ({
      ...prev,
      finishCodes: (prev.finishCodes || []).map((code: any, i: number) => i === index ? { ...code, isActive: !code.isActive } : code)
    }));
  };

  const handleResetFinishCodes = () => {
    setFormData((prev: any) => ({
      ...prev,
      finishCodes: [...DEFAULT_FINISH_CODES]
    }));
  };

  const handleSaveQueue = async () => {
    try {
      let targetQueueId = editingQueue?._id;
      if (editingQueue) {
        // Güncelle
        const updated = await updateQueue(editingQueue._id, formData);
        if (updated && updated._id) targetQueueId = updated._id;
      } else {
        // Yeni ekle
        const created = await createQueue(formData);
        if (created && created._id) targetQueueId = created._id;
      }

      // Explicitly update finish-codes for the queue if queue ID is present
      if (targetQueueId && formData.finishCodes) {
        const token = localStorage.getItem('accessToken');
        await fetch(`/api/queues/${targetQueueId}/finish-codes`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ finishCodes: formData.finishCodes })
        });
      }

      await fetchQueues();
      setOpenDialog(false);
      setFormData({
        name: '',
        description: '',
        greetingMessage: 'Hoş geldiniz. Lütfen bekleyiniz, en kısa sürede size yardımcı olacağız.',
        asteriskName: '',
        locationIds: [],
        priority: 'medium',
        maxWaitTime: 300,
        status: 'active',
        outboundPrefix: '',
        assignedAgentIds: [],
        assignedSipIds: [],
        customerCardApiUrl: '',
        customerCardDatasetListUrl: '',
        customerCardDatasetSchemaUrl: '',
        customerCardDatasetAddUrl: '',
        customerCardSelectedDatasetId: '',
        finishCodes: [...DEFAULT_FINISH_CODES]
      });
    } catch (error) {
      console.error('Error saving queue:', error);
    }
  };

  const handleDeleteQueue = async (queueId: string) => {
    try {
      await deleteQueue(queueId);
    } catch (error) {
      console.error('Error deleting queue:', error);
    }
  };

  const handleToggleStatus = async (queueId: string) => {
    try {
      const queue = queues.find(q => q._id === queueId);
      if (queue) {
        await updateQueue(queueId, { status: queue.status === 'active' ? 'inactive' : 'active' });
      }
    } catch (error) {
      console.error('Error toggling queue status:', error);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      maxHeight: '100vh',
      overflowY: 'auto',
      backgroundImage: 'url(./bg.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundRepeat: 'no-repeat',
      p: 3
    }}>
      {/* Geri İkonu */}
      <Box sx={{ mb: 3 }}>
        <Box 
          sx={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            cursor: 'pointer',
            p: 1,
            borderRadius: 2,
            backgroundColor: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.2)',
              transform: 'translateX(-2px)'
            }
          }}
          onClick={() => window.history.back()}
        >
          <IconArrowLeft size={24} color="white" />
        </Box>
      </Box>

      {/* Ana İçerik */}
      <Box sx={{ px: 10 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight="700" color="white" sx={{ fontFamily: 'Monaco, monospace', textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>
            Kuyruk Yönetimi
          </Typography>
          <Button
            variant="contained"
            startIcon={<IconUserPlus size={20} />}
            onClick={handleAddQueue}
            sx={{
              bgcolor: '#FF9800',
              '&:hover': { bgcolor: '#F57C00' },
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontFamily: 'Monaco, monospace',
              fontWeight: 600
            }}
          >
            Yeni Kuyruk Oluştur
          </Button>
        </Box>

        {/* İstatistikler */}
        <Grid2 container spacing={3} sx={{ mb: 3 }}>
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #E0E0E0',
              background: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Monaco, monospace', color: '#2C3E50' }}>
                      {queues.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Monaco, monospace' }}>
                      Toplam Kuyruk
                    </Typography>
                  </Box>
                  <IconMusic size={40} color="#FF9800" />
                </Box>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #E0E0E0',
              background: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Monaco, monospace', color: '#2C3E50' }}>
                      {queues.filter(q => q.status === 'active').length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Monaco, monospace' }}>
                      Aktif Kuyruk
                    </Typography>
                  </Box>
                  <IconCheck size={40} color="#4CAF50" />
                </Box>
              </CardContent>
            </Card>
        </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #E0E0E0',
              background: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Monaco, monospace', color: '#2C3E50' }}>
                      {queues.reduce((sum, q) => sum + (q.totalCalls || 0), 0)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Monaco, monospace' }}>
                      Toplam Çağrı
                    </Typography>
                  </Box>
                  <IconPhone size={40} color="#1976D2" />
                </Box>
              </CardContent>
            </Card>
          </Grid2>

          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ 
              borderRadius: 3, 
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              border: '1px solid #E0E0E0',
              background: 'white'
            }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" fontWeight="700" sx={{ fontFamily: 'Monaco, monospace', color: '#2C3E50' }}>
                      {Math.round(queues.reduce((sum, q) => sum + (q.avgWaitTime || 0), 0) / queues.length)}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', fontFamily: 'Monaco, monospace' }}>
                      Ort. Bekleme (dk)
                    </Typography>
                  </Box>
                  <IconFileText size={40} color="#9C27B0" />
                </Box>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>

        {/* Filtreler */}
        <Card sx={{ mb: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.1)', border: '1px solid #E0E0E0', background: 'white' }}>
          <CardContent sx={{ p: 3 }}>
            <Grid2 container spacing={2} alignItems="center">
              <Grid2 size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  placeholder="Kuyruk ara..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <IconSearch size={20} color="#666" style={{ marginRight: 8 }} />
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      fontFamily: 'Monaco, monospace'
                    }
                  }}
                />
              </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Durum Filtresi</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Durum Filtresi"
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="all">Tüm Durumlar</MenuItem>
                  <MenuItem value="active">Aktif</MenuItem>
                  <MenuItem value="inactive">Pasif</MenuItem>
                </Select>
              </FormControl>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  label={`Toplam: ${queues.length}`}
                  color="primary"
                  variant="outlined"
                />
                <Chip
                  label={`Aktif: ${queues.filter(q => q.status === 'active').length}`}
                  color="success"
                  variant="outlined"
                />
              </Box>
            </Grid2>
          </Grid2>
        </CardContent>
      </Card>

      {/* Kuyruk Tablosu */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F5F5F5' }}>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Kuyruk Adı</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Lokasyonlar</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Öncelik</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Max Bekleme</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Atanan Temsilciler</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Toplam Çağrı</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>Durum</TableCell>
                <TableCell sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredQueues.map((queue) => (
                <TableRow key={queue._id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body1" fontWeight="500" color="#2C3E50" sx={{ fontFamily: 'Inter, sans-serif' }}>
                        {queue.name}
                      </Typography>
                      <Typography variant="body2" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
                        {queue.description}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(queue.locationIds || []).map((locationId: string) => {
                        const location = locations.find(l => l._id === locationId);
                        return location ? (
                          <Chip
                            key={locationId}
                            label={location.name}
                            size="small"
                            sx={{
                              bgcolor: '#E3F2FD',
                              color: '#1976D2',
                              fontWeight: 500,
                              fontFamily: 'Inter, sans-serif'
                            }}
                          />
                        ) : null;
                      })}
                      {(!queue.locationIds || queue.locationIds.length === 0) && (
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Inter, sans-serif' }}>
                          Lokasyon atanmamış
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getPriorityText(queue.priority)}
                      sx={{
                        bgcolor: getPriorityColor(queue.priority),
                        color: 'white',
                        fontWeight: 500,
                        fontFamily: 'Inter, sans-serif'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
                      {formatWaitTime(queue.maxWaitTime)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {queue.assignedAgentIds?.map((agentData: any, index: number) => {
                        // ✨ Backend can send either string ID or populated object
                        const agentId = typeof agentData === 'string' ? agentData : agentData._id;
                        const agentObj = typeof agentData === 'string'
                          ? users.find(u => u._id === agentData)
                          : agentData;

                        const displayName = agentObj
                          ? `${agentObj.firstName || ''} ${agentObj.lastName || ''}`.trim()
                          : 'Bilinmiyor';

                        return (
                          <Chip
                            key={index}
                            label={displayName}
                            size="small"
                            icon={<IconUsers size={16} />}
                            sx={{
                              bgcolor: '#E3F2FD',
                              color: '#1976D2',
                              fontWeight: 500,
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '0.75rem'
                            }}
                          />
                        );
                      })}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="#666" sx={{ fontFamily: 'Inter, sans-serif' }}>
                      {queue.totalCalls || 0}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={getStatusText(queue.status)}
                        sx={{
                          bgcolor: getStatusColor(queue.status),
                          color: 'white',
                          fontWeight: 500,
                          fontFamily: 'Inter, sans-serif'
                        }}
                      />
                      <Switch
                        checked={queue.status === 'active'}
                        onChange={() => handleToggleStatus(queue._id)}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Düzenle">
                        <IconButton
                          size="small"
                          onClick={() => handleEditQueue(queue)}
                          sx={{ color: '#1976D2' }}
                        >
                          <IconEdit size={20} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sil">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteQueue(queue._id)}
                          sx={{ color: '#F44336' }}
                        >
                          <IconTrash size={20} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Kuyruk Ekleme/Düzenleme Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600 }}>
          {editingQueue ? 'Kuyruk Düzenle' : 'Yeni Kuyruk Oluştur'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              fullWidth
              label="Kuyruk Adı"
              placeholder="Satış Kuyruğu"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="Asterisk Kuyruk Numarası"
              placeholder="orn: 800, 801"
              value={formData.asteriskName}
              onChange={(e) => setFormData({ ...formData, asteriskName: e.target.value })}
              helperText="FreePBX kuyruk numarası (800, 801 vs). Dialer çağrılarında müşteri bu kuyruğa yönlendirilir."
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="Açıklama"
              placeholder="Bu kuyruk için açıklama..."
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <FormControl fullWidth>
              <InputLabel>Lokasyonlar</InputLabel>
              <Select
                multiple
                value={formData.locationIds}
                onChange={(e) => setFormData({ ...formData, locationIds: e.target.value as string[] })}
                label="Lokasyonlar"
              >
                {locations.map(location => (
                  <MenuItem key={location._id} value={location._id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Öncelik</InputLabel>
              <Select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                label="Öncelik"
              >
                <MenuItem value="high">Yüksek</MenuItem>
                <MenuItem value="medium">Orta</MenuItem>
                <MenuItem value="low">Düşük</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Karşılama Mesajı"
              multiline
              rows={3}
              value={formData.greetingMessage}
              onChange={(e) => setFormData({ ...formData, greetingMessage: e.target.value })}
              placeholder="Arayan kişiye okunacak karşılama mesajını girin..."
              helperText="Bu mesaj çağrı geldiğinde otomatik olarak okunacak, ardından müzik çalacaktır."
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="Maksimum Bekleme Süresi (saniye)"
              type="number"
              value={formData.maxWaitTime}
              onChange={(e) => setFormData({ ...formData, maxWaitTime: parseInt(e.target.value) })}
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <TextField
              fullWidth
              label="Giden Arama Prefix (Opsiyonel)"
              value={formData.outboundPrefix}
              onChange={(e) => setFormData({ ...formData, outboundPrefix: e.target.value })}
              placeholder="Örn: 123"
              helperText="Bu kuyruktan yapılan aramalar için telefon numarasının başına eklenecek prefix. Örnek: 123 yazarsanız, 05380745873 yerine 12305380745873 aranır."
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />
            <FormControl fullWidth>
              <InputLabel>Durum</InputLabel>
              <Select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                label="Durum"
              >
                <MenuItem value="active">Aktif</MenuItem>
                <MenuItem value="inactive">Pasif</MenuItem>
              </Select>
            </FormControl>

            {/* Kuyruğa Özel Sonuç / Bitirme Kodları */}
            <Divider sx={{ mt: 2, mb: 2 }}>
              <Chip label="Kuyruğa Özel Sonuç Kodları (Finish Codes)" color="primary" size="small" />
            </Divider>

            <Box sx={{ bgcolor: '#f8fafc', p: 2, borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'Inter, sans-serif' }}>
                  Tanımlı Sonuç Kodları ({(formData as any).finishCodes?.length || 0})
                </Typography>
                <Button size="small" variant="text" onClick={handleResetFinishCodes} sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
                  Varsayılanlara Sıfırla
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {((formData as any).finishCodes || []).map((code: any, index: number) => (
                  <Chip
                    key={index}
                    label={`${code.name}${code.requireNote ? ' 📝' : ''}`}
                    onDelete={() => handleRemoveFinishCode(index)}
                    onClick={() => handleToggleFinishCodeActive(index)}
                    sx={{
                      bgcolor: code.isActive ? code.color || '#3b82f6' : '#cbd5e1',
                      color: 'white',
                      fontWeight: 600,
                      opacity: code.isActive ? 1 : 0.6,
                      fontSize: '0.8rem'
                    }}
                  />
                ))}
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                * Not zorunlu olan kodlarda 📝 simgesi bulunur. Tıklayarak aktif/pasif yapabilir veya silebilirsiniz.
              </Typography>

              {/* Yeni Kod Ekleme Alanı */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder="Yeni Sonuç Kodu Adı"
                  value={newCodeName}
                  onChange={(e) => setNewCodeName(e.target.value)}
                  sx={{ flexGrow: 1, minWidth: '160px', bgcolor: 'white' }}
                />
                <input
                  type="color"
                  value={newCodeColor}
                  onChange={(e) => setNewCodeColor(e.target.value)}
                  style={{ width: 36, height: 36, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                  title="Kod Rengi Seçin"
                />
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={newCodeRequireNote}
                      onChange={(e) => setNewCodeRequireNote(e.target.checked)}
                    />
                  }
                  label={<Typography variant="caption">Not Zorunlu</Typography>}
                  sx={{ mr: 0 }}
                />
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleAddFinishCode}
                  disabled={!newCodeName.trim()}
                  sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}
                >
                  Ekle
                </Button>
              </Box>
            </Box>

            {/* CRM Entegrasyonu Ayarları */}
            <Divider sx={{ mt: 2, mb: 2 }}>
              <Chip label="CRM Entegrasyonu" size="small" />
            </Divider>

            <TextField
              fullWidth
              label="Müşteri Kartı API URL"
              value={formData.customerCardApiUrl}
              onChange={(e) => setFormData({ ...formData, customerCardApiUrl: e.target.value })}
              placeholder="https://api.example.com/customer"
              helperText="Gelen aramada müşteri bilgilerini çekmek için kullanılacak API adresi. Parametreler: phone, queueId"
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />

            <TextField
              fullWidth
              label="Dataset Listesi URL"
              value={formData.customerCardDatasetListUrl}
              onChange={(e) => setFormData({ ...formData, customerCardDatasetListUrl: e.target.value })}
              placeholder="https://api.example.com/dataset-names"
              helperText="CRM sistemindeki dataset listesini almak için kullanılacak GET endpoint"
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />

            <TextField
              fullWidth
              label="Dataset Şema URL"
              value={formData.customerCardDatasetSchemaUrl}
              onChange={(e) => setFormData({ ...formData, customerCardDatasetSchemaUrl: e.target.value })}
              placeholder="https://api.example.com/dataset-schema/:id"
              helperText="Seçilen dataset'in şemasını almak için kullanılacak GET endpoint. :id parametresi dataset ID ile değiştirilir"
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />

            <TextField
              fullWidth
              label="Dataset Kayıt Ekleme URL"
              value={formData.customerCardDatasetAddUrl}
              onChange={(e) => setFormData({ ...formData, customerCardDatasetAddUrl: e.target.value })}
              placeholder="https://api.example.com/dataset-add/:id"
              helperText="Yeni kayıt eklemek için kullanılacak POST endpoint. :id parametresi dataset ID ile değiştirilir"
              sx={{ fontFamily: 'Inter, sans-serif' }}
            />

            {/* Atanmış Temsilciler Bölümü */}
            {editingQueue && (
              <Box sx={{ mt: 2 }}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconUsers size={20} />
                  Atanmış Temsilciler ({formData.assignedAgentIds.length})
                </Typography>
                {formData.assignedAgentIds.length > 0 ? (
                  <List sx={{ bgcolor: '#f8f9fa', borderRadius: 2, maxHeight: 300, overflow: 'auto' }}>
                    {formData.assignedAgentIds.map((agentId, index) => {
                      const agent = users.find(u => u._id === agentId);
                      if (!agent) return null;

                      return (
                        <ListItem
                          key={agentId}
                          sx={{
                            borderBottom: index < formData.assignedAgentIds.length - 1 ? '1px solid #e0e0e0' : 'none',
                            '&:hover': { bgcolor: '#e3f2fd' }
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: '#1976D2',
                              width: 36,
                              height: 36,
                              mr: 2,
                              fontSize: '0.875rem'
                            }}
                          >
                            {`${agent.firstName?.[0] || ''}${agent.lastName?.[0] || ''}`}
                          </Avatar>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ fontFamily: 'Inter, sans-serif', fontWeight: 500 }}>
                                {`${agent.firstName || ''} ${agent.lastName || ''}`.trim()}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'Inter, sans-serif' }}>
                                {agent.email}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Chip
                              label={agent.role === 'agent' ? 'Temsilci' : agent.role === 'supervisor' ? 'Supervisor' : 'Admin'}
                              size="small"
                              sx={{
                                bgcolor: agent.role === 'agent' ? '#4CAF50' : agent.role === 'supervisor' ? '#FF9800' : '#F44336',
                                color: 'white',
                                fontWeight: 500,
                                fontSize: '0.7rem'
                              }}
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                ) : (
                  <Box sx={{
                    bgcolor: '#f8f9fa',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    border: '1px dashed #ccc'
                  }}>
                    <IconUser size={32} color="#999" style={{ marginBottom: 8 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'Inter, sans-serif' }}>
                      Bu kuyruğa henüz temsilci atanmamış
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenDialog(false)} sx={{ fontFamily: 'Inter, sans-serif' }}>
            İptal
          </Button>
          <Button
            onClick={handleSaveQueue}
            variant="contained"
            sx={{
              bgcolor: '#FF9800',
              '&:hover': { bgcolor: '#F57C00' },
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600
            }}
          >
            {editingQueue ? 'Güncelle' : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default QueueManagement;
